import { CompareProducts } from '@iheartjane/menu';
import {
  Box,
  Button,
  FiltersIcon,
  Flex,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { useState } from 'react';

import type {
  AlgoliaProduct,
  JaneSearchState,
  SelectFilterGroup,
} from '@jane/search/types';
import { LoadingWrapper } from '@jane/shared/components';

import { ActiveFilterProvider } from '../activeFilterProvider';
import { FilterBarGroup } from '../filterBarGroup';
import { FilterModal } from '../filterModal/filterModal';
import { FilterSearch } from '../filterSearch/filterSearch';
import { FilterSort } from '../filterSort/filterSort';
import { ActiveFilters } from './activeFilters';
import {
  CondensedFilterRadiusWrapper,
  CondensedSortWrapper,
  StyledFilterWrapper,
  StyledProductFilterBar,
  StyledSortWrapper,
} from './productFilterBar.styles';
import type { ProductFilterBarProps } from './productFilterBar.types';
import { ProductFilterRadius } from './productFilterRadius';

export const ProductFilterBar = ({
  activeFilters,
  condensedView = false,
  currentSort,
  hideCounts = false,
  isLoading,
  isLoadingFacets,
  filters,
  listView,
  maxRadius,
  modalSearchState,
  noPadding = false,
  onChange,
  onDeselect,
  onFilterOpen,
  onQuery,
  onRadius,
  onSort,
  onView,
  radius,
  setModalSearchState,
  showCompare = false,
  sortOptions,
  searchId,
  totalResults,
  variant = 'product',
}: ProductFilterBarProps) => {
  const isMobile = useMobileMediaQuery({});

  const [open, setOpen] = useState(false);
  /* NOTE product search filters do not have the same styling and onView is only passed for store menus */
  const isMenu = Boolean(onView);

  const renderedBarFilters = condensedView
    ? null
    : filters?.map((filterGroup) => {
        return (
          <FilterBarGroup
            key={filterGroup.key}
            filterGroup={filterGroup as SelectFilterGroup}
            hideCounts={hideCounts}
            isLoading={isLoading}
            onChange={onChange}
            onDeselect={onDeselect}
            onOpen={onFilterOpen}
            totalResults={totalResults}
            variant={variant}
          />
        );
      });

  return (
    <ActiveFilterProvider activeFilters={activeFilters}>
      <Box id="results-filter">
        {isLoadingFacets && (
          <LoadingWrapper
            variant="filters"
            isLoading
            pb={8}
            pt={isMenu ? 0 : 24}
            pl={isMenu ? (isMobile ? 24 : 64) : 24}
            pr={!isMobile ? (isMenu ? 64 : 24) : 0}
          />
        )}
        {!isLoadingFacets && (
          <>
            {condensedView ? (
              <>
                <Flex
                  gap={16}
                  px={noPadding ? 0 : isMobile ? 24 : 64}
                  justifyContent={isMobile ? 'flex-start' : 'space-between'}
                  width="100%"
                >
                  <Flex gap={16} width="100%">
                    <FilterSearch onSearch={onQuery} searchId={searchId} />
                    <Button
                      startIcon={<FiltersIcon />}
                      onClick={() => setOpen(true)}
                      variant="tertiary"
                      ariaLabel={`Open ${variant} filters`}
                      disabled={!filters.length}
                      label="Filters"
                      full={isMobile}
                    />
                  </Flex>
                  <CondensedSortWrapper
                    justifyContent={isMobile ? 'flex-start' : 'flex-end'}
                    width={isMobile ? '100%' : 'auto'}
                  >
                    {showCompare && <CompareProducts.Button mr={8} />}
                    <FilterSort
                      currentSort={currentSort}
                      onSort={onSort}
                      sortOptions={sortOptions}
                      variant="product"
                    />
                    {onRadius && !isMobile && (
                      <ProductFilterRadius
                        maxRadius={maxRadius}
                        onRadius={onRadius}
                        radius={radius}
                      />
                    )}
                  </CondensedSortWrapper>
                </Flex>
                {onRadius && isMobile && (
                  <CondensedFilterRadiusWrapper>
                    <ProductFilterRadius
                      maxRadius={maxRadius}
                      onRadius={onRadius}
                      radius={radius}
                    />
                  </CondensedFilterRadiusWrapper>
                )}
              </>
            ) : (
              <StyledProductFilterBar isMenu={isMenu}>
                <StyledFilterWrapper>
                  <FilterSearch onSearch={onQuery} searchId={searchId} />

                  <div>
                    <Button.Icon
                      icon={<FiltersIcon />}
                      onClick={() => setOpen(true)}
                      variant="tertiary"
                      ariaLabel={`Open ${variant} filters`}
                      disabled={!filters.length}
                    />
                  </div>
                  {showCompare && isMobile && <CompareProducts.Button />}
                  {isMobile && (
                    <FilterSort
                      currentSort={currentSort}
                      onSort={onSort}
                      sortOptions={sortOptions}
                      variant="product"
                    />
                  )}
                  {renderedBarFilters}
                </StyledFilterWrapper>

                <StyledSortWrapper>
                  {showCompare && !isMobile && (
                    <CompareProducts.Button mr={8} />
                  )}
                  {!isMobile && (
                    <FilterSort
                      currentSort={currentSort}
                      onSort={onSort}
                      sortOptions={sortOptions}
                      variant="product"
                    />
                  )}
                  {onRadius && (
                    <ProductFilterRadius
                      maxRadius={maxRadius}
                      onRadius={onRadius}
                      radius={radius}
                    />
                  )}
                </StyledSortWrapper>
              </StyledProductFilterBar>
            )}
          </>
        )}
        <ActiveFilters
          activeFilters={activeFilters}
          onChange={onChange}
          onDeselect={onDeselect}
          noPadding={noPadding}
          isMenu={isMenu}
        />
      </Box>
      <FilterModal<JaneSearchState<AlgoliaProduct>>
        filters={filters}
        hideCounts={hideCounts}
        isLoading={isLoading}
        listView={listView}
        onChange={onChange}
        onDeselect={onDeselect}
        searchState={modalSearchState}
        onView={onView}
        open={open}
        setOpen={setOpen}
        setSearchState={setModalSearchState}
        totalResults={totalResults}
        variant={variant}
      />
    </ActiveFilterProvider>
  );
};
